import React, { Suspense, useRef, useState, useReducer, useEffect, useCallback } from 'react';
import {
    Row,
    Col
} from 'react-bootstrap';
import {useLocation, useHistory} from "react-router-dom";
import SwipeableBottomSheet from 'react-swipeable-bottom-sheet';
import ModalLoading from '../components/ModalLoading';
import FiltersMenu from '../components/FiltersMenu';
import FiltersBadges from '../components/FiltersBadges';
import PopupFicha from '../components/PopupFicha';
import BottomFiltersMenuHandler from '../components/BottomFiltersMenuHandler';
import IntervaloDataFilter from '../components/IntervaloDataFilter';
import CONSTANTS from '../constants';
import {
    resgataOpcoes,
    fazBusca,
    filtraFichas,
    filtraIntervaloData
} from '../api';
import {
    getFiltersFromURL,
    getIntervaloFichas,
    updateUrl
} from '../utils/utils';

const Panorama = React.lazy(() => import('../components/Panorama'));
const Mapa = React.lazy(() => import('../components/Mapa'));
const Listagem = React.lazy(() => import('../components/Listagem'));

const getGeoData = async (filters) => {
    const recorteTerritorial = (filters.recorteTerritorial !== '-1')
        ? filters.recorteTerritorial
        : 'municipio';

    const fileJSON = (recorteTerritorial === 'municipio')
        ? `municipio/${filters.localizacao[0].slug.slice(-2)}.json`
        : `${recorteTerritorial}.json`;
    return await import(`../geojson/${fileJSON}`);
}

export default function MainScreen(props) {
    const initialState = {
        menuDisabled: false,
        filters: CONSTANTS.emptyFilters,
        fichas: {},
        fichasVisiveis: {},
        geoData: null,
        opcoes: null,
        opcoesVisiveis: null,
        qtdeFichasVisiveis: 0,
        fichaSelected: null,
        intervaloData: {},
        isMobile: window.innerWidth <= CONSTANTS.MOBILE_BREAKPOINT
    };
    const [loading, setLoading] = useState(false);
    const reducer = (state, newState) => {
        const ret = {
            ...state,
            ...newState,
            filters: {
                ...state.filters,
                ...newState.filters
            }
        };
        return ret;
    };
    const [state, setState] = useReducer(reducer, initialState);
    const mainContentRef = useRef();
    const menuFiltrosRef = useRef();
    const mounted = useRef(true);
    const location = useLocation();
    const history = useHistory();

    // const getGeoJSON = async (recorte) => {
    //     let recorte = (recorteTerritorial.current)
    //         ? recorteTerritorial.current
    //         : '';
    //     if (!recorte) {
    //         recorte = 'municipio';
    //     }
    //     const shape = (recorte === 'municipio')
    //         ? SHAPES[props.filters.localizacao[0].slug.slice(-2)]
    //         : SHAPES[recorte];
    //     return shape;
    // };

    useEffect(() => {
        const init = async () => {
            setLoading(true);

            const isMobile = window.innerWidth <= CONSTANTS.MOBILE_BREAKPOINT;
            window.addEventListener("resize", () => setState({
                isMobile: window.innerWidth <= CONSTANTS.MOBILE_BREAKPOINT
            }));

            mounted.current = false;
            const opcoes = await resgataOpcoes();
            const fichas = await fazBusca();
            const intervaloData = getIntervaloFichas(fichas, state.isMobile);

            let newState = {
                opcoes: opcoes,
                opcoesVisiveis: opcoes,
                fichas: fichas,
                fichasVisiveis: fichas,
                intervaloData: intervaloData,
                isMobile: isMobile
            }

            const searchParams = new URLSearchParams(location.search);

            if (Symbol.iterator in Object(searchParams.keys())) {
                const filters = getFiltersFromURL(searchParams, opcoes);

                let {
                    fichasVisiveis,
                    qtdeFichasVisiveis,
                    nFamiliasAmeacadas,
                    nFamiliasRemovidas,
                    nFamiliasSuspensaoDefinitiva,
                    intervaloData,
                    opcoesVisiveis
                } = filtraFichas(filters, fichas, opcoes, isMobile);

                if (
                    filters.modo === 'mapa' &&
                    filters.recorteTerritorial === '-1' &&
                    qtdeFichasVisiveis > CONSTANTS.MAPA_THRESHOLD.PTS[isMobile ? 'mobile' : 'desktop']
                ) {
                    filters.recorteTerritorial = CONSTANTS.MAPA_THRESHOLD.RECORTE;
                }

                if (filters.intervaloDataValue.length > 0) {
                    const ret = filtraIntervaloData(filters.intervaloDataValue, fichasVisiveis);
                    fichasVisiveis = ret.fichasVisiveis;
                    qtdeFichasVisiveis = ret.qtdeFichasVisiveis;
                }

                newState = {
                    ...newState,
                    filters: filters,
                    fichasVisiveis: fichasVisiveis,
                    qtdeFichasVisiveis: qtdeFichasVisiveis,
                    nFamiliasAmeacadas: nFamiliasAmeacadas,
                    nFamiliasRemovidas: nFamiliasRemovidas,
                    nFamiliasSuspensaoDefinitiva: nFamiliasSuspensaoDefinitiva,
                    intervaloData: intervaloData,
                    opcoesVisiveis: opcoesVisiveis,
                    geoData: null,
                    menuDisabled: searchParams.get('menuDisabled') !== null
                };

                if (
                    filters.recorteTerritorial !== '-1' ||
                    (
                        filters.localizacao.length === 1 &&
                        filters.localizacao[0].tax === 'municipio'
                    )
                ) {
                    newState.geoData = await getGeoData(filters);
                }
            }

            setLoading(false);
            setState(newState);

            return () => {
                mounted.current = false;
            }
        }
        if (mounted.current) {
            init();
        }
    }, [location.search, state.isMobile]);

    // console.log({MainsScreenStateAreasTematicas: state.filters.areas_tematicas});

    const updateFilters = useCallback((filters) => {
        // setLoading(true);
        if (filters.hasOwnProperty('modo') && filters.modo !== state.filters.modo) {
            toggleSwipeableDrawer(false);
        }

        const newFilters = {
            ...state.filters,
            ...filters
        };
        localStorage.setItem('modoVisualizacao', newFilters.modo);

        let {
            fichasVisiveis,
            qtdeFichasVisiveis,
            nFamiliasAmeacadas,
            nFamiliasRemovidas,
            nFamiliasSuspensaoDefinitiva,
            intervaloData,
            opcoesVisiveis
        } = filtraFichas(newFilters, state.fichas, state.opcoes, state.isMobile);

        if (
            newFilters.modo === 'mapa' &&
            newFilters.recorteTerritorial === '-1' &&
            qtdeFichasVisiveis > CONSTANTS.MAPA_THRESHOLD.PTS[state.isMobile ? 'mobile' : 'desktop']
        ) {
            newFilters.recorteTerritorial = CONSTANTS.MAPA_THRESHOLD.RECORTE;
        }

        if (newFilters.intervaloDataValue.length > 0) {
            const ret = filtraIntervaloData(newFilters.intervaloDataValue, fichasVisiveis);
            fichasVisiveis = ret.fichasVisiveis;
            qtdeFichasVisiveis = ret.qtdeFichasVisiveis;
        }

        const newState = {
            filters: newFilters,
            fichasVisiveis: fichasVisiveis,
            qtdeFichasVisiveis: qtdeFichasVisiveis,
            nFamiliasAmeacadas: nFamiliasAmeacadas,
            nFamiliasRemovidas: nFamiliasRemovidas,
            nFamiliasSuspensaoDefinitiva: nFamiliasSuspensaoDefinitiva,
            intervaloData: intervaloData,
            opcoesVisiveis: opcoesVisiveis,
            geoData: null
        };

        updateUrl(history, newFilters);

        if (
            newFilters.modo === 'mapa' &&
            (
                newFilters.recorteTerritorial !== '-1' ||
                (
                    newFilters.localizacao.length === 1 &&
                    newFilters.localizacao[0].tax === 'municipio'
                )
            )
        ) {
            setLoading(true);
            getGeoData(newFilters).then((geoData) => {
                newState.geoData = geoData;
                setState(newState);
                setLoading(false);
            });
            return;
        }

        setState(newState);

        setTimeout(() => {
            if (newFilters.modo === 'listagem') {
                mainContentRef.current.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                });
            }
        }, 350);
    }, [state.filters, state.fichas, state.opcoes, state.isMobile, history]);

    const toggleSwipeableDrawer = (open) => {
        if (!open) {
            setTimeout(() => {
                // setShowMap(true);
            }, 50);
            return;
        }

        // setShowMap(false);
    }

    return (
        <>
            <Row id="AERContainer" className={`${state.filters.modo}${state.menuDisabled ? '' : ' withMenu'}`}>
                {!state.menuDisabled && !state.isMobile &&
                    <Col key="sideBar" id="AERSidebarColumn">
                        <FiltersMenu
                            className = "AERSidebar"
                            key = "AERSidebar"
                            filters = {state.filters}
                            fichas = {state.fichasVisiveis}
                            qtdeFichas = {state.qtdeFichasVisiveis}
                            opcoes = {state.opcoesVisiveis}
                            intervaloData = {state.isMobile ? state.intervaloData : null}
                            isMobile = {state.isMobile}
                            menuFiltrosRef = {menuFiltrosRef}
                            onChange = {updateFilters}
                        />
                    </Col>
                }

                {Object.keys(state.fichasVisiveis).length > 0 &&
                    <Col key="mainContent" id="AERMainContent" ref={mainContentRef}>
                        {state.filters.modo === 'panorama' &&
                            <Suspense fallback={<ModalLoading show={true} />}>
                                <Panorama
                                    key = 'panorama'
                                    filters = {state.filters}
                                    fichas = {state.fichasVisiveis}
                                    qtdeFichasVisiveis = {state.qtdeFichasVisiveis}
                                    nFamiliasAmeacadas = {state.nFamiliasAmeacadas}
                                    nFamiliasRemovidas = {state.nFamiliasRemovidas}
                                    nFamiliasSuspensaoDefinitiva = {state.nFamiliasSuspensaoDefinitiva}
                                    isMobile = {state.isMobile}
                                    opcoes = {state.opcoesVisiveis}
                                    onChange = {updateFilters}
                                />
                            </Suspense>
                        }
                        {state.filters.modo === 'mapa' &&
                            <Suspense fallback={<ModalLoading show={true} />}>
                                <Mapa
                                    key = 'mapa'
                                    menuDisabled = {state.menuDisabled}
                                    filters = {state.filters}
                                    fichas = {state.fichasVisiveis}
                                    qtdeFichasVisiveis = {state.qtdeFichasVisiveis}
                                    nFamiliasAmeacadas = {state.nFamiliasAmeacadas}
                                    nFamiliasRemovidas = {state.nFamiliasRemovidas}
                                    geoData = {state.geoData}
                                    icones = {state.icones}
                                    opcoes = {state.opcoesVisiveis}
                                    isMobile = {state.isMobile}
                                    onChange = {updateFilters}
                                    setFicha = {ficha => { setState({fichaSelected: ficha}); }}
                                />
                            </Suspense>
                        }
                        {state.filters.modo === 'listagem' &&
                            <Suspense fallback={<ModalLoading show={true} />}>
                                <Listagem
                                    key = 'listagem'
                                    filters = {state.filters}
                                    fichas = {state.fichasVisiveis}
                                    qtdeFichasVisiveis = {state.qtdeFichasVisiveis}
                                    nFamiliasAmeacadas = {state.nFamiliasAmeacadas}
                                    nFamiliasRemovidas = {state.nFamiliasRemovidas}
                                    isMobile = {state.isMobile}
                                    opcoes = {state.opcoesVisiveis}
                                />
                            </Suspense>
                        }
                    </Col>
                }
            </Row>

            <PopupFicha
                ficha={state.fichaSelected}
                opcoes={state.opcoesVisiveis}
                menuDisabled={state.menuDisabled}
                onChange={(filters) => {
                    if (filters) {
                        updateFilters(filters);
                    }
                    setState({fichaSelected: null});
                }}
            />

            {/*
                !state.menuDisabled &&
                !state.isMobile &&
                Object.keys(state.intervaloData).length > 0 && state.intervaloData.step > 0 &&

                <div key="intervaloDataWrapper" id="intervaloDataWrapper">
                    <IntervaloDataFilter
                        intervaloDataValue={state.filters.intervaloDataValue}
                        intervaloData={state.intervaloData}
                        onChangeValue={updateFilters}
                        isMobile={state.isMobile}
                    />
                </div>
            */}

            {(state.menuDisabled || state.isMobile) &&
                <FiltersBadges
                    filters={state.filters}
                    menuDisabled={state.menuDisabled}
                    onChange = {updateFilters}
                />
            }

            <ModalLoading show={loading} />

            {!state.menuDisabled && state.isMobile &&
                <SwipeableBottomSheet
                    className="SwipeableBottomSheet"
                    overflowHeight={88}
                    marginTop={'10vh'}
                    scrollTopAtClose={false}
                    style={{zIndex: 1000}}
                    onChange={(isOpen) => {
                        if (!isOpen) {
                            menuFiltrosRef.current.scrollTo({top: 0, left: 0});
                        }
                    }}
                >
                    <BottomFiltersMenuHandler qtde={state.qtdeFichasVisiveis} />

                    <FiltersMenu
                        className = "bottomFiltersMenu"
                        key = "bottomFiltersMenu"
                        filters = {state.filters}
                        fichas = {state.fichasVisiveis}
                        qtdeFichas = {state.qtdeFichasVisiveis}
                        opcoes = {state.opcoesVisiveis}
                        intervaloData = {state.isMobile ? state.intervaloData : null}
                        isMobile = {state.isMobile}
                        menuFiltrosRef = {menuFiltrosRef}
                        onChange = {updateFilters}
                    />
                </SwipeableBottomSheet>
            }
        </>
    );
}
